import { useEffect, useMemo, useState, useCallback } from 'react';
import dayjs from 'dayjs';
import { SortDirection, getTodayActivity } from '@api/cut';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from '@features/redux/store';
import { useAlert } from '@hooks/useAlert';
import { useNameMapQuery } from '@features/redux/apis/org';
import { setLastUpdated } from '@features/redux/slices/reportSlice';
import { Box, Typography, useMediaQuery, useTheme, Pagination } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { BasePage } from './bases/BasePage';
import { BreadcrumbSubBar } from '@components/BreadcrumbSubBar';
import { useOrgs } from '@hooks/useOrgs';
import { useScrollablePagination } from '@hooks/useScrollablePagination';
import { MobileTodaysCutCard, MobileTodaysCutCardSkeleton } from '@components/MobileTodaysCutCard';
import { TotalCuts } from '@components/reports/today/TotalCuts';

import {
    colors,
    typography as typographyTokens,
    radii,
    Button,
    FilterPanel,
    FilterSection,
    TableCard,
    DataTable as DesignDataTable,
} from '@components/design-system';
import type { DataTableColumn } from '@components/design-system/components/Table/DataTable';
import { MultiSelectFilter } from '@components/MultiSelectFilter';

const PAGE_SIZE = 50;
const MOBILE_PAGE_SIZE = 20;

type TodayCutsData = {
    TotalRecords: number;
    ResellerCompanyName: string;
    LocationName: string;
    ParentCompanyName: string;
    totalCuts: number;
    successfulCuts: number;
    warrantyClaimCuts: number;
};

type FilterInput = {
    companies: { [key: string]: boolean };
    resellers: { [key: string]: boolean };
    locations: { [key: string]: boolean };
};

export const TodaysCuts = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const dispatch = useDispatch();
    const { setAlert } = useAlert();

    const { IdToken } = useSelector((state: RootState) => state.auth);
    const tz = useSelector((state: RootState) => state.timezone.tz);
    const { companies: companiesMap, resellers: resellersMap, locations: locationsMap } = useOrgs();
    const { data: nameMap } = useNameMapQuery({});

    // Convert IdMap to array format for MultiSelectFilter
    const companies = useMemo(
        () => Object.entries(companiesMap).map(([id, name]) => ({ id, name })),
        [companiesMap]
    );
    const resellers = useMemo(
        () => Object.entries(resellersMap).map(([id, name]) => ({ id, name })),
        [resellersMap]
    );
    const locations = useMemo(
        () => Object.entries(locationsMap).map(([id, name]) => ({ id, name })),
        [locationsMap]
    );

    const [todayCutData, setTodayCutData] = useState<TodayCutsData[]>([]);
    const [loadingCuts, setLoadingCuts] = useState<boolean>(false);
    const [filtersOpen, setFiltersOpen] = useState(false);

    const [filters, setFilters] = useState<FilterInput>({
        companies: {},
        resellers: {},
        locations: {},
    });

    const [draftFilters, setDraftFilters] = useState<FilterInput>({
        companies: {},
        resellers: {},
        locations: {},
    });

    const [pageNum, setPageNum] = useState<number>(1);
    const { scrollContainerRef, scrollToTop } = useScrollablePagination();

    const handlePageChange = useCallback((newPage: number) => {
        setPageNum(newPage);
        scrollToTop();
    }, [scrollToTop]);

    const [sortOptions, setSortOptions] = useState<
        Record<'company' | 'location' | 'reseller' | 'totalcuts', SortDirection>
    >({
        company: SortDirection.Ascending,
        location: SortDirection.Ascending,
        reseller: SortDirection.Ascending,
        totalcuts: SortDirection.Descending,
    });
    const [priority, setPriority] = useState<'totalcuts' | 'org'>('totalcuts');

    const pageSize = isMobile ? MOBILE_PAGE_SIZE : PAGE_SIZE;

    const getCutData = async () => {
        setLoadingCuts(true);
        try {
            // "Today" in the user's selected timezone (the wire layer expands
            // these Y-M-D strings to UTC instants for the BE BETWEEN clause).
            const today = dayjs.tz().format('YYYY-MM-DD');

            const todaysCuts = (await getTodayActivity({
                token: IdToken!,
                tz,
                params: {
                    pageSize,
                    pageNum: pageNum - 1,
                    startDate: today,
                    endDate: today,
                    companyIds: Object.keys(filters.companies)
                        .filter((key) => filters.companies[key])
                        .join(','),
                    resellerIds: Object.keys(filters.resellers)
                        .filter((key) => filters.resellers[key])
                        .join(','),
                    locationIds: Object.keys(filters.locations)
                        .filter((key) => filters.locations[key])
                        .join(','),
                    sortCompany: sortOptions.company,
                    sortLocation: sortOptions.location,
                    sortReseller: sortOptions.reseller,
                    sortTotalCuts: sortOptions.totalcuts,
                    priority,
                },
            })) as TodayCutsData[];

            dispatch(setLastUpdated());
            setTodayCutData(todaysCuts);
        } catch (e) {
            setAlert('Could not get todays cut data', 'error');
        } finally {
            setLoadingCuts(false);
        }
    };

    useEffect(() => {
        getCutData();
    }, [pageNum, sortOptions, filters, tz]);

    const totalRecords = todayCutData[0]?.TotalRecords || 0;
    const numPages = useMemo(() => Math.ceil(totalRecords / pageSize) || 1, [totalRecords, pageSize]);

    const handleOpenFilters = () => {
        setDraftFilters(filters);
        setFiltersOpen(true);
    };

    const handleCloseFilters = () => {
        // Apply filters when closing the drawer
        setFilters(draftFilters);
        setPageNum(1);
        setFiltersOpen(false);
    };

    const handleResetFilters = () => {
        // Reset and apply immediately
        setDraftFilters({ companies: {}, resellers: {}, locations: {} });
        setFilters({ companies: {}, resellers: {}, locations: {} });
        setPageNum(1);
    };

    const setSortDirection = (key: 'company' | 'location' | 'reseller' | 'totalcuts', value: SortDirection) => {
        setSortOptions({ ...sortOptions, [key]: value });
        setPageNum(1);
        setPriority(key === 'totalcuts' ? 'totalcuts' : 'org');
    };

    const infoText = `Showing ${Math.min((pageNum - 1) * pageSize + 1, totalRecords)}-${Math.min(
        pageNum * pageSize,
        totalRecords
    )} of ${totalRecords} locations`;

    // Table columns for desktop
    const columns: DataTableColumn<TodayCutsData>[] = [
        {
            field: 'ParentCompanyName',
            headerName: 'Company',
            flex: 1,
            minWidth: 150,
        },
        {
            field: 'ResellerCompanyName',
            headerName: 'Reseller',
            flex: 1,
            minWidth: 150,
        },
        {
            field: 'LocationName',
            headerName: 'Location',
            flex: 1,
            minWidth: 150,
        },
        {
            field: 'totalCuts',
            headerName: 'Total Cuts',
            width: 110,
            align: 'center',
        },
        {
            field: 'successfulCuts',
            headerName: 'Successful',
            width: 140,
            renderCell: ({ row }) => (
                <TotalCuts totalCuts={row.totalCuts} numCutType={row.successfulCuts} color='success' />
            ),
        },
        {
            field: 'warrantyClaimCuts',
            headerName: 'Warranty',
            width: 140,
            renderCell: ({ row }) => (
                <TotalCuts totalCuts={row.totalCuts} numCutType={row.warrantyClaimCuts} color='error' />
            ),
        },
    ];

    // Mobile view
    if (isMobile) {
        return (
            <BasePage>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        flex: 1,
                        minHeight: 0,
                        overflow: 'hidden',
                    }}>
                    {/* Row 1: Title + Filter button */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            px: 2,
                            pt: 2,
                            pb: 1.5,
                            backgroundColor: colors.neutral[0],
                            borderBottom: `1px solid ${colors.neutral[200]}`,
                        }}>
                        <Typography
                            sx={{
                                fontSize: '1.125rem',
                                fontWeight: 700,
                                color: colors.neutral[900],
                            }}>
                            Today's Cuts
                        </Typography>
                        <Button
                            variant='secondary'
                            size='small'
                            onClick={handleOpenFilters}
                            startIcon={<FilterListIcon />}
                            sx={{
                                minWidth: 'auto',
                                px: 1,
                                '& .MuiButton-startIcon': { margin: 0 },
                            }}>
                            {''}
                        </Button>
                    </Box>

                    {/* Row 2: Info text */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            px: 2,
                            py: 1.5,
                            backgroundColor: colors.neutral[0],
                            borderBottom: `1px solid ${colors.neutral[200]}`,
                        }}>
                        <Typography
                            sx={{
                                fontSize: '0.8125rem',
                                color: colors.neutral[600],
                            }}>
                            {totalRecords > 0 ? infoText : 'No locations found'}
                        </Typography>
                    </Box>

                    {/* Cards Container */}
                    <Box
                        ref={scrollContainerRef}
                        sx={{
                            flex: 1,
                            minHeight: 0,
                            overflowY: 'auto',
                            p: 1.5,
                            backgroundColor: colors.neutral[50],
                        }}>
                        {loadingCuts || !nameMap ? (
                            Array.from({ length: MOBILE_PAGE_SIZE }).map((_, i) => (
                                <MobileTodaysCutCardSkeleton key={i} />
                            ))
                        ) : todayCutData.length === 0 ? (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    height: '100%',
                                    color: colors.neutral[500],
                                }}>
                                <Typography>No data available for today</Typography>
                            </Box>
                        ) : (
                            todayCutData.map((item, idx) => (
                                <MobileTodaysCutCard key={`${item.LocationName}-${idx}`} data={item} />
                            ))
                        )}
                    </Box>

                    {/* Mobile Pagination */}
                    {numPages > 1 && (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                py: 1.5,
                                backgroundColor: colors.neutral[0],
                                borderTop: `1px solid ${colors.neutral[200]}`,
                            }}>
                            <Pagination
                                count={numPages}
                                page={pageNum}
                                onChange={(_, page) => handlePageChange(page)}
                                color='primary'
                                size='small'
                                siblingCount={0}
                                boundaryCount={1}
                                sx={{
                                    '& .MuiPaginationItem-root': {
                                        borderRadius: `${radii.md}px`,
                                        minWidth: 32,
                                    },
                                }}
                            />
                        </Box>
                    )}

                    {/* Filter Panel */}
                    <FilterPanel
                        open={filtersOpen}
                        onClose={handleCloseFilters}
                        onReset={handleResetFilters}>
                        <FilterSection title='Organization'>
                            <MultiSelectFilter
                                label='Company'
                                options={companies}
                                value={draftFilters.companies}
                                onChange={(company) => {
                                    const companyObj = company as { id: string; name: string };
                                    setDraftFilters((prev) => ({
                                        ...prev,
                                        companies: { ...prev.companies, [companyObj.id]: !prev.companies[companyObj.id] },
                                    }));
                                }}
                                getLabelValue={(opt) => opt.name}
                                idKey='id'
                                search
                                margin={false}
                            />
                            <MultiSelectFilter
                                label='Reseller'
                                options={resellers}
                                value={draftFilters.resellers}
                                onChange={(reseller) => {
                                    const resellerObj = reseller as { id: string; name: string };
                                    setDraftFilters((prev) => ({
                                        ...prev,
                                        resellers: { ...prev.resellers, [resellerObj.id]: !prev.resellers[resellerObj.id] },
                                    }));
                                }}
                                getLabelValue={(opt) => opt.name}
                                idKey='id'
                                search
                                margin={false}
                            />
                            <MultiSelectFilter
                                label='Location'
                                options={locations}
                                value={draftFilters.locations}
                                onChange={(location) => {
                                    const locationObj = location as { id: string; name: string };
                                    setDraftFilters((prev) => ({
                                        ...prev,
                                        locations: { ...prev.locations, [locationObj.id]: !prev.locations[locationObj.id] },
                                    }));
                                }}
                                getLabelValue={(opt) => opt.name}
                                idKey='id'
                                search
                                margin={false}
                            />
                        </FilterSection>
                    </FilterPanel>
                </Box>
            </BasePage>
        );
    }

    // Desktop view
    return (
        <BasePage>
            <BreadcrumbSubBar />
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    minHeight: 0,
                    backgroundColor: colors.neutral[100],
                    p: 2,
                }}>
                <TableCard
                    title="Today's Cuts"
                    actions={
                        <Button
                            variant='secondary'
                            size='small'
                            onClick={handleOpenFilters}
                            startIcon={<FilterListIcon />}>
                            Filters
                        </Button>
                    }
                    sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                    {/* Table */}
                    <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                        <DesignDataTable
                            rows={todayCutData}
                            columns={columns}
                            getRowId={(row) => `${row.LocationName}-${row.ParentCompanyName}`}
                            totalRows={totalRecords}
                            page={pageNum}
                            pageSize={pageSize}
                            onPageChange={handlePageChange}
                            loading={loadingCuts || !nameMap}
                            height='100%'
                            infoText={infoText}
                        />
                    </Box>
                </TableCard>

                {/* Filter Panel */}
                <FilterPanel
                    open={filtersOpen}
                    onClose={handleCloseFilters}
                    onReset={handleResetFilters}>
                    <FilterSection title='Organization'>
                        <MultiSelectFilter
                            label='Company'
                            options={companies}
                            value={draftFilters.companies}
                            onChange={(company) => {
                                const companyObj = company as { id: string; name: string };
                                setDraftFilters((prev) => ({
                                    ...prev,
                                    companies: { ...prev.companies, [companyObj.id]: !prev.companies[companyObj.id] },
                                }));
                            }}
                            getLabelValue={(opt: { id: string; name: string }) => opt.name}
                            idKey='id'
                            search
                            margin={false}
                        />
                        <MultiSelectFilter
                            label='Reseller'
                            options={resellers}
                            value={draftFilters.resellers}
                            onChange={(reseller) => {
                                const resellerObj = reseller as { id: string; name: string };
                                setDraftFilters((prev) => ({
                                    ...prev,
                                    resellers: { ...prev.resellers, [resellerObj.id]: !prev.resellers[resellerObj.id] },
                                }));
                            }}
                            getLabelValue={(opt: { id: string; name: string }) => opt.name}
                            idKey='id'
                            search
                            margin={false}
                        />
                        <MultiSelectFilter
                            label='Location'
                            options={locations}
                            value={draftFilters.locations}
                            onChange={(location) => {
                                const locationObj = location as { id: string; name: string };
                                setDraftFilters((prev) => ({
                                    ...prev,
                                    locations: { ...prev.locations, [locationObj.id]: !prev.locations[locationObj.id] },
                                }));
                            }}
                            getLabelValue={(opt: { id: string; name: string }) => opt.name}
                            idKey='id'
                            search
                            margin={false}
                        />
                    </FilterSection>
                </FilterPanel>
            </Box>
        </BasePage>
    );
};
